<template>
  <FormKit
    type="form"
    ref="formRef"
    :actions="false" 
    @submit="() => handleButton('primaryCtaButton')"
  >
    <div class="cta-card flex flex-col items-center">
      <img v-if="icon" :src="icon" alt="icon" class="cta-card-icon h-20 w-20 mb-4" />
      <div v-if="title" class="cta-card-title text-center text-xl-bold mb-6">{{ title }}</div>
      <div v-if="hasBeforeSlotContent || hasDefaultSlotContent || hasAfterSlotContent" class="cta-card-content-container w-full">
        <slot v-if="hasBeforeSlotContent" name="beforeContent"></slot>
        <slot v-if="hasDefaultSlotContent"><RichText :field="{ value: content }" class="cta-card-content m-0 p-0" /></slot>
        <slot v-if="hasAfterSlotContent" name="afterContent"></slot>
      </div>
      <div
        v-if="primaryButtonLabel || secondaryButtonLabel"
        class="cta-card-buttons mt-6 flex flex-col items-center w-full gap-4"
      >
        <button
          v-if="primaryButtonLabel"
          type="submit"
          class="cta-card-button flex px-3 py-2 justify-center items-center self-stretch text-base btn btn-md btn-primary"
          :aria-label="primaryButtonLabel"
        >
          {{ primaryButtonLabel }}
        </button>
        <button
          v-if="secondaryButtonLabel"
          type="button"
          class="cta-card-button flex px-3 py-2 justify-center items-center self-stretch text-base btn-md btn btn-secondary"
          :aria-label="secondaryButtonLabel"
          @click.stop.prevent="() => handleButton('secondaryCtaButton')"
        >
          {{ secondaryButtonLabel }}
        </button>
      </div>
    </div>
  </FormKit>
</template>

<script setup lang="ts">
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { useSlots, computed, ref } from 'vue';
import { FormKit } from '@formkit/vue';

interface CTACardProps {
  icon?: string;
  title?: string;
  content?: string;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
}

const props = defineProps<CTACardProps>();
const formRef = ref(null);
const emit = defineEmits<(e: 'buttonClick', type: 'primaryCtaButton' | 'secondaryCtaButton', valid?: boolean ) => void>();

const slots = useSlots();
const hasDefaultSlotContent = computed(() => {
  const defaultContent = slots.default?.();
  return !!(defaultContent && defaultContent.length) || !!(props.content && props.content.length);
});
const hasBeforeSlotContent = computed(() => {
  const beforeContent = slots.beforeContent?.();
  return !!(beforeContent && beforeContent.length);
});
const hasAfterSlotContent = computed(() => {
  const afterContent = slots.afterContent?.();
  return !!(afterContent && afterContent.length);
});

async function handleButton(
  type: 'primaryCtaButton' | 'secondaryCtaButton'
) {
    if (type === 'primaryCtaButton') {
      const isValid = formRef.value?.node?.context?.state.valid;
      emit('buttonClick', type, isValid);
    } else if (type === 'secondaryCtaButton') {
      emit('buttonClick', type);
    }
  }
</script>
<!-- external styles defined under call-to-action-card.css -->
